import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import categoryService from 'services/categoryService';
import { useStorefrontContext } from 'context/StorefrontContext';
import { CategoryMenuNode } from 'types/category';
import { NavItem } from 'types/storefront';
import { ROUTES } from 'config/routes';
import HeaderSelectors from './HeaderSelectors';
import ColorModeToggle from './ColorModeToggle';

interface CategoryDrawerProps {
  open: boolean;
  onClose: () => void;
}

const defaultNav: NavItem[] = [
  { labelKey: 'nav.catalog', url: ROUTES.CATALOG },
  { labelKey: 'nav.promotions', url: ROUTES.PROMOTIONS },
  { labelKey: 'nav.news', url: ROUTES.NEWS },
  { labelKey: 'nav.brands', url: ROUTES.BRANDS },
];

const CategoryMenuList: React.FC<{
  items: CategoryMenuNode[];
  onClose: () => void;
  nested?: boolean;
}> = ({ items, onClose, nested = false }) => {
  if (!items.length) return null;

  return (
    <List dense disablePadding>
      {items.map((item) =>
        item.children?.length ? (
          <Accordion key={item.id} disableGutters elevation={0} square>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ pl: nested ? 1 : 2 }}>
              <Typography
                component={RouterLink}
                to={item.url}
                onClick={onClose}
                sx={{ textDecoration: 'none', color: 'inherit', fontWeight: nested ? 500 : 600 }}
              >
                {item.label}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ py: 0, pl: 1 }}>
              <CategoryMenuList items={item.children} onClose={onClose} nested />
            </AccordionDetails>
          </Accordion>
        ) : (
          <ListItemButton
            key={item.id}
            component={RouterLink}
            to={item.url}
            onClick={onClose}
            sx={{ pl: nested ? 2 : 2 }}
          >
            <ListItemText primary={item.label} />
          </ListItemButton>
        )
      )}
    </List>
  );
};

const CategoryDrawer: React.FC<CategoryDrawerProps> = ({ open, onClose }) => {
  const { t } = useTranslation('common');
  const { navigation, features } = useStorefrontContext();
  const [categories, setCategories] = useState<CategoryMenuNode[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    categoryService
      .getCategoryMenu()
      .then((data) => {
        if (!cancelled) setCategories(data);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const navItems = (navigation.header.length > 0 ? navigation.header : defaultNav).filter((item) => {
    if (item.url.includes('promociones') && !features.promotionsEnabled) return false;
    if (item.url.includes('marcas') && !features.brandsEnabled) return false;
    return true;
  });

  const labelFor = (item: NavItem) => item.label || (item.labelKey ? t(item.labelKey) : item.url);

  return (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 300, sm: 340 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('nav.menu')}
          </Typography>
          <IconButton onClick={onClose} aria-label={t('nav.closeMenu')}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List dense disablePadding>
          {navItems.map((item) =>
            item.children?.length ? (
              <Accordion key={`${item.url}-${item.order ?? item.label}`} disableGutters elevation={0} square>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography
                    component={RouterLink}
                    to={item.url}
                    onClick={onClose}
                    sx={{ textDecoration: 'none', color: 'inherit', fontWeight: 600 }}
                  >
                    {labelFor(item)}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ py: 0 }}>
                  <List dense disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={`${child.url}-${child.order ?? child.label}`}
                        component={RouterLink}
                        to={child.url}
                        onClick={onClose}
                      >
                        <ListItemText primary={labelFor(child)} />
                      </ListItemButton>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ) : (
              <ListItemButton
                key={`${item.url}-${item.order ?? item.label}`}
                component={RouterLink}
                to={item.url}
                onClick={onClose}
              >
                <ListItemText primary={labelFor(item)} />
              </ListItemButton>
            )
          )}
        </List>
        <Divider />
        <Typography variant="subtitle2" sx={{ px: 2, pt: 2, pb: 1, fontWeight: 700 }}>
          {t('catalog.categories')}
        </Typography>
        <Box sx={{ flexGrow: 1, overflow: 'auto', px: 1, pb: 2 }}>
          <CategoryMenuList items={categories} onClose={onClose} />
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            {t('nav.colorMode')}
          </Typography>
          <ColorModeToggle />
          <Typography variant="caption" color="text.secondary" display="block" mb={1} mt={2}>
            {t('nav.preferences')}
          </Typography>
          <HeaderSelectors />
        </Box>
      </Box>
    </Drawer>
  );
};

export default CategoryDrawer;
